/**
 * Hub landing page (/hubs/:id): title + description, then the Hub's items as a
 * list in the same sections as its rail fly-out (its own items, then "Also in
 * <Hub>", marked with their home). Every row links to the item's one canonical
 * URL. Home has its own page (HomeHubPage), so does Knowledgebase
 * (KnowledgebasePage, SOP search); Lupa's Hub is the chat page itself.
 * Layout: HubSpot-style list instead of cards (Jon, 2026-11-03).
 */
import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faThumbtack } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../contexts/AuthContext';
import { DWA } from '../theme/dwaTokens';
import { canSeeHub, findHub } from '../hubs/hubs';
import type { Hub, HubItem } from '../hubs/hubs';
import { hubSections, isAdminOnly, isComingSoon } from '../hubs/hubNav';
import { GradientPage } from '../hubs/HubCards';
import { HubLayout } from '../hubs/HubHeader';
import { SoonBadge } from '../hubs/HubMenu';
import { usePinnedItems } from '../hubs/preferences';
import { HomeHubPage } from './HomeHubPage';
import { KnowledgebasePage } from './KnowledgebasePage';

/** Page title block shared by Hub pages: icon tile, title, one line. */
export const HubPageHeader: React.FC<{ hub: Hub; title?: string; subtitle?: React.ReactNode; action?: React.ReactNode }> = ({
  hub,
  title,
  subtitle,
  action,
}) => (
  <header style={{ display: 'flex', alignItems: 'flex-start', gap: '0.9rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
    <span
      aria-hidden="true"
      style={{
        width: 44,
        height: 44,
        borderRadius: DWA.radius,
        background: `linear-gradient(135deg, ${DWA.dark}, ${DWA.darkSoft})`,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <FontAwesomeIcon icon={hub.icon} style={{ color: DWA.gold, fontSize: '1.1rem' }} />
    </span>
    <div style={{ flex: '1 1 14rem', minWidth: 0 }}>
      <h1 style={{ fontSize: 'clamp(1.35rem, 4vw, 1.7rem)', fontWeight: 700, margin: '0 0 0.2rem', color: '#ffffff' }}>{title ?? hub.title}</h1>
      <p style={{ margin: 0, color: DWA.onDarkMuted, fontSize: '0.92rem', lineHeight: 1.5 }}>{subtitle ?? hub.description}</p>
    </div>
    {action}
  </header>
);

const ItemRow: React.FC<{ item: HubItem; hub: Hub; isHome: boolean; pinned: boolean; onTogglePin: () => void }> = ({
  item,
  hub,
  isHome,
  pinned,
  onTogglePin,
}) => {
  const soon = isComingSoon(item);
  const home = findHub(item.home);
  const adminOnly = isAdminOnly(item) && hub.id !== 'admin';
  return (
    <li className="ow-hub-row" data-testid={`hub-item-${item.id}`} style={{ opacity: soon ? 0.75 : 1 }}>
      <div className="ow-hub-row-title" style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', flexWrap: 'wrap', minWidth: 0 }}>
        {item.route && !soon ? (
          <Link to={item.route} className="ow-hub-row-link" style={{ fontWeight: 700, color: DWA.ink, textDecoration: 'none', fontSize: '0.95rem' }}>
            {item.title}
          </Link>
        ) : (
          <span aria-disabled="true" style={{ fontWeight: 700, color: DWA.textSecondary, fontSize: '0.95rem' }}>
            {item.title}
          </span>
        )}
        {soon && <SoonBadge tone="light" />}
        {adminOnly && (
          <span title="Admin only" style={{ fontSize: '0.7rem', color: DWA.textSecondary, display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
            <FontAwesomeIcon icon={faLock} aria-hidden="true" />
            Admin only
          </span>
        )}
      </div>
      <div className="ow-hub-row-desc" style={{ minWidth: 0 }}>
        {item.description && <p style={{ margin: 0, color: DWA.textSecondary, fontSize: '0.85rem', lineHeight: 1.5 }}>{item.description}</p>}
        {!isHome && home && (
          <p style={{ margin: '0.15rem 0 0', fontSize: '0.75rem', fontWeight: 700, color: DWA.goldInk }}>from {home.title}</p>
        )}
        {soon && item.scope && item.scope.length > 0 && (
          <ul style={{ margin: '0.3rem 0 0', paddingLeft: '1.1rem', fontSize: '0.8rem', color: DWA.textSecondary, lineHeight: 1.5 }}>
            {item.scope.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ul>
        )}
      </div>
      <div className="ow-hub-row-action">
        {item.route && !soon && (
          <button
            type="button"
            onClick={onTogglePin}
            aria-pressed={pinned}
            aria-label={pinned ? `Unpin ${item.title}` : `Pin ${item.title}`}
            title={pinned ? 'Unpin' : 'Pin (shows on Home and under Pinned)'}
            style={{
              width: 30,
              height: 30,
              borderRadius: '50%',
              border: `1px solid ${pinned ? DWA.gold : DWA.lineLight}`,
              cursor: 'pointer',
              background: pinned ? DWA.gold : '#ffffff',
              color: pinned ? DWA.onGold : DWA.textMuted,
            }}
          >
            <FontAwesomeIcon icon={faThumbtack} style={{ fontSize: '0.75rem' }} />
          </button>
        )}
      </div>
    </li>
  );
};

export const HubPage: React.FC = () => {
  const { hubId } = useParams();
  const { user } = useAuth();
  const hub = findHub(hubId);
  const { isPinned, togglePin } = usePinnedItems(user?.id);

  if (!hub || !canSeeHub(user, hub)) return <Navigate to="/" replace />;
  if (hub.id === 'lupa') return <Navigate to={hub.route} replace />;
  if (hub.id === 'home') return <HomeHubPage />;
  if (hub.id === 'knowledgebase') return <KnowledgebasePage />;

  const sections = hubSections(hub, user);

  return (
    <HubLayout>
      <GradientPage>
        <main style={{ maxWidth: 1100, margin: '0 auto', padding: '1.5rem clamp(1rem, 3vw, 1.75rem) 6rem' }}>
          <HubPageHeader hub={hub} />
          {sections.length === 0 && <p style={{ color: DWA.onDarkMuted }}>Nothing here yet.</p>}
          {sections.map((section) => (
            <section
              key={section.id}
              aria-labelledby={`hub-section-${section.id}`}
              data-testid={`hub-section-${section.id}`}
              style={{
                background: '#ffffff',
                border: `1px solid ${DWA.lineLight}`,
                borderRadius: DWA.radius,
                marginBottom: '1.25rem',
                overflow: 'hidden',
              }}
            >
              <h2
                id={`hub-section-${section.id}`}
                style={{
                  margin: 0,
                  padding: '0.75rem 1.1rem',
                  fontSize: '0.72rem',
                  fontWeight: 700,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  color: DWA.textSecondary,
                  borderBottom: `1px solid ${DWA.lineLight}`,
                  background: '#fbfbfa',
                }}
              >
                {section.id === 'own' ? `In ${hub.title}` : section.label}
              </h2>
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {section.listings.map(({ item, isHome }) => (
                  <ItemRow
                    key={item.id}
                    item={item}
                    hub={hub}
                    isHome={isHome}
                    pinned={isPinned(item.id)}
                    onTogglePin={() => togglePin(item.id)}
                  />
                ))}
              </ul>
            </section>
          ))}
        </main>
      </GradientPage>
    </HubLayout>
  );
};

export default HubPage;
